/**
 * TREATX-09C-OPS-VISIBILITY-DASHBOARD-01 (2026-06-15)
 *
 * Content Protection Ops Visibility — read-only admin dashboard surfacing
 * the already-existing canonical leakage-prevention telemetry.
 *
 * Reuses ONLY existing canonical endpoints:
 *   GET  /api/v2/guard/flagged-threads
 *   GET  /api/v2/guard/leakage-events
 *   POST /api/v2/guard/leakage-events/:event_id/resolve
 *
 * No new collection, no new endpoint, no new service. UI-only.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, ShieldCheck, ShieldAlert, ShieldOff, AlertTriangle,
  Loader2, CheckCircle2, EyeOff, Activity,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Toaster } from '../../components/Toaster';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

function fmt(iso) {
  if (!iso) return '—';
  try {
    return formatDateTimeLocale(iso);
  } catch { return iso; }
}

function severityBadge(score) {
  const s = Number(score) || 0;
  if (s >= 70) return <Badge className="bg-rose-100 text-rose-700 border-rose-200" data-testid={`risk-band-high`}>high</Badge>;
  if (s >= 30) return <Badge className="bg-amber-100 text-amber-700 border-amber-200" data-testid={`risk-band-medium`}>medium</Badge>;
  return <Badge className="bg-slate-100 text-slate-600 border-slate-200" data-testid={`risk-band-low`}>low</Badge>;
}

function actionBadge(label) {
  const v = (label || '').toLowerCase();
  if (v.includes('block')) return <Badge className="bg-rose-100 text-rose-700 border-rose-200">blocked</Badge>;
  if (v.includes('mask')) return <Badge className="bg-amber-100 text-amber-700 border-amber-200">masked</Badge>;
  if (v.includes('flag')) return <Badge className="bg-orange-100 text-orange-700 border-orange-200">flagged</Badge>;
  return <Badge variant="outline" className="text-[10px]">{label || 'detected'}</Badge>;
}

const ContentProtectionOps = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [threads, setThreads] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tr, ev] = await Promise.all([
        api.get('/v2/guard/flagged-threads', { params: { limit: 100 } }),
        api.get('/v2/guard/leakage-events', { params: { limit: 100 } }),
      ]);
      // Both endpoints return either {threads|events:[...]} or plain arrays.
      setThreads(((tr.data && (tr.data.threads || tr.data.items || tr.data)) || []));
      setEvents(((ev.data && (ev.data.events || ev.data.items || ev.data)) || []));
    } catch (e) {
      setError((e.response && e.response.data && (e.response.data.detail || e.response.data.error)) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const arr = Array.isArray(events) ? events : [];
    let masked = 0, blocked = 0, flagged = 0, high = 0, sum = 0, n = 0;
    arr.forEach((e) => {
      const reasons = (e.trigger_reasons || []).map((r) => String(r).toLowerCase());
      const prob = Number(e.leakage_probability || e.risk_score || 0);
      if (prob >= 70) high += 1;
      sum += prob; n += 1;
      if (reasons.some((r) => r.includes('payment'))) blocked += 1;
      else if (reasons.length) masked += 1;
      flagged += 1;
    });
    return {
      flagged_threads: Array.isArray(threads) ? threads.length : 0,
      contacts_masked: masked,
      payments_blocked: blocked,
      high_risk: high,
      avg_risk: n ? Math.round((sum / n) * 10) / 10 : 0,
      events_total: n,
    };
  }, [threads, events]);

  const filteredEvents = useMemo(() => {
    const arr = Array.isArray(events) ? events : [];
    if (tab === 'all') return arr;
    return arr.filter((e) => {
      const reasons = (e.trigger_reasons || []).map((r) => String(r).toLowerCase());
      const prob = Number(e.leakage_probability || e.risk_score || 0);
      if (tab === 'blocked') return reasons.some((r) => r.includes('payment'));
      if (tab === 'masked') return reasons.length && !reasons.some((r) => r.includes('payment'));
      if (tab === 'high') return prob >= 70;
      return true;
    });
  }, [events, tab]);

  const resolveEvent = async (id) => {
    try {
      await api.post(`/v2/guard/leakage-events/${id}/resolve`);
      toast({ title: 'Event resolved' });
      load();
    } catch (e) {
      const msg = (e.response && e.response.data && (e.response.data.detail || e.response.data.error)) || e.message;
      toast({ title: 'Could not resolve', description: msg, variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="content-protection-ops-page">
      <Toaster />

      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/treatx-admin/notification-ops')} data-testid="cp-back-to-ops">
              <ArrowLeft className="h-4 w-4 mr-1" /> Notification Ops
            </Button>
            <div>
              <h1 className="text-xl font-semibold flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-600" />
                Content Protection
              </h1>
              <p className="text-xs text-slate-500">
                Read-only telemetry from <code>/api/v2/guard/*</code> (MessagingGuardService).
                No projection. No cache.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="cp-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Stat row */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
          <Card data-testid="cp-stat-flagged"><CardContent className="p-4">
            <div className="text-xs uppercase text-slate-500">Flagged threads</div>
            <div className="text-2xl font-semibold">{stats.flagged_threads}</div>
            <div className="text-[11px] text-slate-500 mt-1">Auto-flagged by guard</div>
          </CardContent></Card>
          <Card data-testid="cp-stat-masked"><CardContent className="p-4">
            <div className="text-xs uppercase text-slate-500">Contacts masked</div>
            <div className="text-2xl font-semibold">{stats.contacts_masked}</div>
            <div className="text-[11px] text-slate-500 mt-1">ALLOW_WITH_MASKING events</div>
          </CardContent></Card>
          <Card data-testid="cp-stat-blocked"><CardContent className="p-4">
            <div className="text-xs uppercase text-slate-500">Payments blocked</div>
            <div className="text-2xl font-semibold">{stats.payments_blocked}</div>
            <div className="text-[11px] text-slate-500 mt-1">BLOCK events</div>
          </CardContent></Card>
          <Card data-testid="cp-stat-high"><CardContent className="p-4">
            <div className="text-xs uppercase text-slate-500">High-risk events</div>
            <div className="text-2xl font-semibold">{stats.high_risk}</div>
            <div className="text-[11px] text-slate-500 mt-1">Risk score ≥ 70</div>
          </CardContent></Card>
          <Card data-testid="cp-stat-avg"><CardContent className="p-4">
            <div className="text-xs uppercase text-slate-500">Avg risk</div>
            <div className="text-2xl font-semibold">{stats.avg_risk}</div>
            <div className="text-[11px] text-slate-500 mt-1">Across {stats.events_total} events</div>
          </CardContent></Card>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList data-testid="cp-tabs">
            <TabsTrigger value="all" data-testid="cp-tab-all">
              <Activity className="h-4 w-4 mr-1" /> All
            </TabsTrigger>
            <TabsTrigger value="masked" data-testid="cp-tab-masked">
              <EyeOff className="h-4 w-4 mr-1" /> Masked
            </TabsTrigger>
            <TabsTrigger value="blocked" data-testid="cp-tab-blocked">
              <ShieldOff className="h-4 w-4 mr-1" /> Blocked
            </TabsTrigger>
            <TabsTrigger value="high" data-testid="cp-tab-high">
              <AlertTriangle className="h-4 w-4 mr-1" /> High risk
            </TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Recent leakage events</CardTitle>
                <CardDescription>
                  Sourced from <code>GET /api/v2/guard/leakage-events</code>. No sensitive content shown — only canonical trigger labels and risk scores.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {loading ? (
                  <div className="py-8 flex items-center justify-center text-slate-500" data-testid="cp-loading">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading telemetry…
                  </div>
                ) : error ? (
                  <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm" data-testid="cp-error">
                    {error}
                  </div>
                ) : filteredEvents.length === 0 ? (
                  <div className="py-10 text-center text-slate-500" data-testid="cp-empty">
                    <ShieldCheck className="h-7 w-7 mx-auto mb-2 text-slate-300" />
                    <div className="text-sm">No matching events.</div>
                  </div>
                ) : (
                  <div className="border rounded-xl overflow-hidden" data-testid="cp-events-table">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr className="text-left text-xs uppercase text-slate-500">
                          <th className="px-3 py-2">When</th>
                          <th className="px-3 py-2">Thread</th>
                          <th className="px-3 py-2">Action</th>
                          <th className="px-3 py-2">Triggers</th>
                          <th className="px-3 py-2">Risk</th>
                          <th className="px-3 py-2 text-right"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEvents.map((e, i) => {
                          const reasons = e.trigger_reasons || [];
                          const isPayment = reasons.some((r) => String(r).toLowerCase().includes('payment'));
                          const risk = Number(e.leakage_probability || e.risk_score || 0);
                          const id = e.id || e._id || e.event_id || `idx-${i}`;
                          const status = (e.status || '').toLowerCase();
                          return (
                            <tr key={id} className="border-t hover:bg-slate-50" data-testid={`cp-event-row-${id}`}>
                              <td className="px-3 py-2 text-xs text-slate-600">{fmt(e.created_at || e.timestamp || e.flagged_at)}</td>
                              <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{(e.thread_id || '—').slice(0, 12)}…</td>
                              <td className="px-3 py-2">
                                {isPayment ? actionBadge('blocked') : (reasons.length ? actionBadge('masked') : actionBadge('flagged'))}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex flex-wrap gap-1">
                                  {reasons.slice(0, 4).map((r, k) => (
                                    <Badge key={k} variant="outline" className="text-[10px]">{r}</Badge>
                                  ))}
                                  {reasons.length > 4 && (
                                    <Badge variant="outline" className="text-[10px]">+{reasons.length - 4}</Badge>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-mono">{risk}</span>
                                  {severityBadge(risk)}
                                </div>
                              </td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                {status !== 'resolved' && (
                                  <Button
                                    size="sm" variant="ghost"
                                    onClick={() => resolveEvent(id)}
                                    data-testid={`cp-event-resolve-${id}`}
                                  >
                                    <CheckCircle2 className="h-4 w-4 mr-1" /> Resolve
                                  </Button>
                                )}
                                {status === 'resolved' && (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">resolved</Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <p className="text-[11px] text-slate-400 mt-6 text-center" data-testid="cp-canonical-note">
          Source of truth: canonical <code>MessagingGuardService</code> via <code>/api/v2/guard/*</code>.
          No projection, no parallel store.
        </p>
      </div>
    </div>
  );
};

export default ContentProtectionOps;
